import type { MeCapacityStatus } from "@/lib/graphql/me-document";

export const DEFAULT_ONBOARDING_BLOCKED_MESSAGE =
  "We are working to add more capacity; we will notify you when service is available again and you can create an API key.";

const fallbackDetail = (state: string) =>
  state === "blocked_global"
    ? "We are working to add more capacity. Your projects stay on the list and we will notify you when service is restored."
    : "We will notify you when this project has been granted access.";

/**
 * User-facing detail line for blocked capacity (banner, tooltips).
 * Prefer API `message` when set; otherwise state-specific fallback (same strings as the capacity notice banner).
 */
export const capacityBlockedDetail = (status: Pick<MeCapacityStatus, "state" | "message">): string => {
  const trimmed = status.message?.trim();
  if (trimmed) {
    return trimmed;
  }
  return fallbackDetail(status.state);
};

/**
 * Onboarding wizard copy when API keys cannot be revealed.
 * Uses `onboarding_message` for global capacity blocks; waitlist uses the standard blocked detail.
 */
export const capacityOnboardingBlockedDetail = (
  status: Pick<MeCapacityStatus, "state" | "message" | "onboarding_message">,
): string => {
  if (status.state === "blocked_waitlist") {
    return capacityBlockedDetail(status);
  }

  const trimmed = status.onboarding_message?.trim();
  if (trimmed !== "") {
    return trimmed;
  }

  return DEFAULT_ONBOARDING_BLOCKED_MESSAGE;
};

/**
 * Detail for tooltips when access is blocked or unknown. If `capacity_status` is not loaded yet,
 * uses the global-capacity fallback so we do not treat keys as safe before `me` resolves.
 * When `state === "granted"`, returns an empty string (callers should not show a capacity tooltip).
 */
export const capacityBlockedDetailForTooltip = (status: MeCapacityStatus | null | undefined): string => {
  if (!status) {
    return fallbackDetail("blocked_global");
  }
  if (status.state === "granted") {
    return "";
  }
  return capacityBlockedDetail(status);
};
